import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { createContext, useEffect, useState } from "react";
import { writeData } from "../../../api";
import { insertPageBlock } from "../../../api/page";
import { useAppIdMap } from "../../../hook/app";
import {
  useComponent,
  useComponents,
  useFetchAllComponentsListener,
  useFetchComponentListener,
} from "../../../hook/component";
import {
  useAddImplementations,
  useFetchImplementationListener,
  useImplementation,
  useImplementationContext,
} from "../../../hook/implementation";
import { useRunImplementationMethod } from "../../../hook/implementation/method";
import { UseMenuProps } from "../../../hook/menu";
import { useInsertBlock } from "../../../hook/page/block";
import { useSelectedNotebookPageIdMap } from "../../../hook/page";
import {
  useFetchAllProtocolsListener,
  useFetchProtocolListener,
  useProtocol,
  useProtocols,
} from "../../../hook/protocol";
import {
  IMPLEMENTATION_LANGUAGE,
  ImplementationContextValue,
  newImplementation,
} from "../../../types/implementation";
import {
  ImplementationMethod,
  ImplementationMethodResponse,
} from "../../../types/implementation/method";
import {
  ImplementationBlock,
  newimplementationBlock,
} from "../../../types/notebook/note/block/implementation";
import { ProtocolSpec } from "../../../types/protocol";
import { DynamicTextField } from "../../dynamicTextField";
import {
  CodeEditorCodeLogs,
  CodeEditorCodeOql,
  CodeEditorCodePython,
  CodeEditorHeader,
  CodeEditorProvider,
  CodeEditorToolbar,
  CodeEditorToolbarCancelButton,
  CodeEditorToolbarCircularProgress,
  CodeEditorToolbarRunButton,
  CodeEditorToolbarSaveButton,
} from "../../editor";
import { NoteBlockContainer } from "../../note";
import { CopyClipboard } from "../../copyClipboard";
import * as R from "ramda";

export const PageImplementationBlockCode: React.FC<{}> = ({}) => {
  const { implementationLanguage } = useImplementationContext();
  return (
    <>
      <CodeEditorHeader>
        <CodeEditorToolbar>
          <CodeEditorToolbarCircularProgress />
          <CodeEditorToolbarCancelButton />
          <CodeEditorToolbarSaveButton />
          <CodeEditorToolbarRunButton />
        </CodeEditorToolbar>
      </CodeEditorHeader>
      {implementationLanguage == IMPLEMENTATION_LANGUAGE.PYTHON && (
        <CodeEditorCodePython />
      )}
      {implementationLanguage == IMPLEMENTATION_LANGUAGE.OQL && (
        <CodeEditorCodeOql />
      )}
    </>
  );
};

export const PageImplementationMethods: React.FC<{}> = ({}) => {
  const {
    selectedMethodIndex,
    setSelectedMethodIndex,
    runResponse,
    onRunCallback,
    functionArgs,
    spec,
    setFunctionArg,
  } = useImplementationContext();
  const functions = spec.functions || [];
  const logsResult = `${JSON.stringify(runResponse?.result, null, 2)}`;
  const logsLogs = `${runResponse?.logs}`;
  const logs = `${logsResult}\n${logsLogs}`;

  return (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
      <Select
        value={selectedMethodIndex}
        onChange={(e, v) => setSelectedMethodIndex(e.target.value as number)}
      >
        {functions.map((funcSpec, index) => {
          return (
            <MenuItem key={index} value={index}>
              {funcSpec.name}
              {"("}
              {funcSpec.args.length}
              {")"}
            </MenuItem>
          );
        })}
      </Select>
      <Box>
        {functionArgs.map((arg, i) => {
          return (
            <Box key={i}>
              <TextField
                value={arg}
                onChange={(e) => {
                  setFunctionArg(i, e.target.value);
                }}
              />
            </Box>
          );
        })}
      </Box>
      <CodeEditorProvider logs={logs} onRunCallback={onRunCallback}>
        <CodeEditorCodeLogs />
      </CodeEditorProvider>
    </Box>
  );
};

export const ImplementationBlockImplementationHeaderTitle: React.FC<{
  value: string | undefined;
}> = ({ value }) => {
  return (
    <Grid item xs={12}>
      <Typography variant="h6">{value}</Typography>
    </Grid>
  );
};

export const ImplementationBlockImplementationHeaderLabel: React.FC<{
  value: string | undefined;
}> = ({ value }) => {
  return (
    <Grid item xs={6} sm={4}>
      <Typography>{value}</Typography>
    </Grid>
  );
};

export const ImplementationBlockImplementationHeaderValue: React.FC<{
  value: string | undefined;
}> = ({ value }) => {
  return (
    <Grid item xs={6} sm={8}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CopyClipboard value={value ?? ""} />
        <Typography>{value ?? ""}</Typography>
      </Box>
    </Grid>
  );
};

export const ImplementationBlockImplementationHeaderLanguageSelect: React.FC =
  () => {
    const { implementationLanguage, setImplementationLanguage } =
      useImplementationContext();
    return (
      <Grid item xs={6} sm={8}>
        <Select
          fullWidth
          value={implementationLanguage}
          onChange={(e) => setImplementationLanguage(e.target.value as any)}
        >
          <MenuItem value={IMPLEMENTATION_LANGUAGE.OQL}>
            Omega Query Language
          </MenuItem>
          <MenuItem value={IMPLEMENTATION_LANGUAGE.PYTHON}>Python</MenuItem>
        </Select>
      </Grid>
    );
  };

export const ImplementationBlockImplementationHeader: React.FC<{}> = () => {
  const {
    implementation: impl,
    component: comp,
    protocol: proto,
    setImplementationName,
  } = useImplementationContext();
  const implName = impl?.name ?? "";
  const compName = comp?.name ?? "";
  const compAppId = comp?.["app-id"] ?? "";
  const compId = comp?.["component-id"] ?? "";
  const protoName = proto?.name ?? "";
  const protoAppId = proto?.["app-id"] ?? "";
  const protoId = proto?.["protocol-id"] ?? "";

  return (
    <Grid container spacing={1}>
      <ImplementationBlockImplementationHeaderLabel value="Component Name" />
      <ImplementationBlockImplementationHeaderValue value={compName} />
      <ImplementationBlockImplementationHeaderLabel value="Component Id" />
      <ImplementationBlockImplementationHeaderValue value={compId} />
      <ImplementationBlockImplementationHeaderLabel value="Component App Id" />
      <ImplementationBlockImplementationHeaderValue value={compAppId} />
      <ImplementationBlockImplementationHeaderLabel value="Protocol Name" />
      <ImplementationBlockImplementationHeaderValue value={protoName} />
      <ImplementationBlockImplementationHeaderLabel value="Protocol Id" />
      <ImplementationBlockImplementationHeaderValue value={protoId} />
      <ImplementationBlockImplementationHeaderLabel value="Protocol App Id" />
      <ImplementationBlockImplementationHeaderValue value={protoAppId} />
      <ImplementationBlockImplementationHeaderLabel value="Implementation Name" />
      <Grid item xs={6} sm={8}>
        <DynamicTextField text={implName} setText={setImplementationName} />
      </Grid>
      <ImplementationBlockImplementationHeaderLabel value="Implementation Language" />
      <ImplementationBlockImplementationHeaderLanguageSelect />
    </Grid>
  );
};

export const ImplementationBlockComponent: React.FC<{}> = () => {
  const {
    implementation: impl,
    setImplementationName,
    runResponse,
    setImplementationCode,
    onRunCallback,
  } = useImplementationContext();
  const implName = impl?.name ?? "";

  const codeEditorProps = {
    codeState: impl?.code ?? "",
    logs: runResponse?.logs ?? "",
    onSaveCallback: setImplementationCode,
    onRunCallback,
  };

  return (
    <CodeEditorProvider {...codeEditorProps}>
      <NoteBlockContainer name="Implementation">
        <ImplementationBlockImplementationHeader />
        <Grid container spacing={1}>
          <Grid item xs={12} md={8}>
            <PageImplementationBlockCode />
          </Grid>
          <Grid item xs={12} md={4}>
            <PageImplementationMethods />
          </Grid>
        </Grid>
      </NoteBlockContainer>
    </CodeEditorProvider>
  );
};

export const ImplementationBlockImplementationById: React.FC<{
  implementationId: string;
}> = ({ implementationId: implId }) => {
  return (
    <ImplementationProvider implementationId={implId}>
      <ImplementationBlockComponent />
    </ImplementationProvider>
  );
};

export const ImplementationBlockImplementation: React.FC<{
  block: ImplementationBlock;
}> = React.memo(({ block }) => {
  const implId = block["implementation-id"];

  return <ImplementationBlockImplementationById implementationId={implId} />;
});

export const ImplementationContext = createContext<
  ImplementationContextValue | undefined
>(undefined);

export const ImplementationProvider: React.FC<{
  implementationId: string | undefined;
  children?: React.ReactNode;
}> = ({ implementationId: implId, children }) => {
  const {
    implementation,
    setImplementationCode,
    setImplementationName,
    implementationLanguage,
    setImplementationLanguage,
  } = useImplementation(implId);
  useFetchImplementationListener(implId);
  useFetchProtocolListener(implementation?.["protocol-id"]);
  useFetchComponentListener(implementation?.["component-id"]);
  const { protocol } = useProtocol(implementation?.["protocol-id"]);
  const { component } = useComponent(implementation?.["component-id"]);
  const [selectedMethodIndex, setSelectedMethodIndex] = useState<number>(0);
  const { runImplementationMethod: runMethod } = useRunImplementationMethod();
  const [runResponse, setRunResponse] = useState<
    ImplementationMethodResponse | undefined
  >(undefined);
  const { appId } = useAppIdMap();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const spec = JSON.parse(protocol?.spec || "{}") as ProtocolSpec;
  const functions = spec?.functions ?? [];
  const selectedMethod =
    selectedMethodIndex in functions
      ? functions[selectedMethodIndex]
      : undefined;
  const [functionArgs, setFunctionArgs] = useState<string[]>([]);
  const setFunctionArg = (index: number, arg: string) => {
    const newFunctionArgs = R.update(index, arg, functionArgs);
    setFunctionArgs(newFunctionArgs);
  };

  const onRunCallback = () => {
    if (!implId) throw new Error("No Implementation");
    const name = selectedMethod?.name;
    const arity = (selectedMethod?.args || []).length;
    const method: ImplementationMethod = {
      "implementation-id": implId,
      "method-arity": arity,
      "method-name": name!,
      type: "omega/query-omega/implementation/method/ImplementationMethod",
      "app-id": appId!,
      "page-id": selectedNotebookPageId!,
    };
    return runMethod(method, functionArgs)
      .then((res) => {
        if (!res["result"]) {
          const newRes: ImplementationMethodResponse = {
            logs: "",
            result: res,
            type: "omega/query-omega/run/implementation/method/ImplementationMethodResponse",
          };
          setRunResponse(newRes);
        } else {
          setRunResponse(res);
        }
      })
      .catch((e) => {
        const runRes: ImplementationMethodResponse = {
          logs: `${e.reason}\n${e.stack}`,
          result: null,
          type: "omega/query-omega/run/implementation/method/ImplementationMethodResponse",
        };
        setRunResponse(runRes);
      });
  };

  useEffect(() => {
    const argsLength = selectedMethod?.args.length;
    if (argsLength === undefined) return;
    const newFunctionArgs = R.range(0, argsLength - 1).map((i) => "");
    setFunctionArgs(newFunctionArgs);
  }, [selectedMethod?.args.length]);

  const contextValue: ImplementationContextValue = {
    implementation,
    protocol,
    component,
    selectedMethodIndex,
    setSelectedMethodIndex,
    onRunCallback,
    setImplementationName,
    setImplementationCode,
    runResponse,
    setRunResponse,
    selectedMethod,
    functionArgs,
    setFunctionArg,
    spec,
    implementationLanguage,
    setImplementationLanguage,
  };

  return (
    <ImplementationContext.Provider value={contextValue}>
      {children}
    </ImplementationContext.Provider>
  );
};
